/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { apDefaultsFromInventory } from './apModels'
import { defaultMountHeight, normalizeMountType } from './apMounts'
import {
  makeOptimizationProgress,
  paceOptimizationStep,
  type FloorPlanOptimizationProgress,
} from './rfOptimizerProgress'
import { assignOptimalChannels, channelPoolForBand } from './rfChannels'
import { computeFloorMetrics, optimizationGrid, type FloorPlanMetrics } from './rfFloorMetrics'
import {
  ensureApMountDefaults,
  geometryModelSummary,
  optimizeGeometryAwarePositions,
  optimizeWallMountOrientations,
  reconcileApAfterMove,
  redistributeApsGeometryAware,
} from './rfOptimizerGeometry'
import { clampToFloorBounds, pointInFloorBounds } from './floorBounds'
import { findGapApPosition, syncApChannels, weakZoneCentroid } from './rfRecommendationFixes'
import { applyOptimizerRecommendations, buildGapApPlacement, simulatedNewApToPlacedAp } from './rfRecommendationApply'
import {
  candidateRank,
  coverageOk,
  interferenceDominant,
  MAX_GOOD_DROP_PCT,
  MAX_INTERF_RISE_PCT,
  MAX_WEAK_RISE_PCT,
  planAcceptable,
  planImproves,
} from './rfOptimizerGuards'
import { rfMetricsAtPoint, computeHeatmapGrid } from './rfSimulation'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'
import type { ApMountType, FloorGeometry, FloorRecommendation, PlacedAp } from './types'

export type { FloorPlanMetrics } from './rfFloorMetrics'

export type FloorPlanOptimizationChange = {
  apId: string
  x?: number
  y?: number
  txPowerDbm?: number
  channel?: number
  mountOrientationDeg?: number
  mountHeightM?: number
}

export type FloorPlanNewAp = {
  apName: string
  x: number
  y: number
  txPowerDbm: number
  channel: number
  mount?: ApMountType
  mountHeightM?: number
  mountOrientationDeg?: number
}

export type FloorPlanOptimizationResult = {
  changes: FloorPlanOptimizationChange[]
  newAps: FloorPlanNewAp[]
  channels: Record<string, number>
  metricsBefore: FloorPlanMetrics
  metricsAfter: FloorPlanMetrics
  summary: string[]
  addressedRecommendations: string[]
  remainingRecommendations: FloorRecommendation[]
}

export type { FloorPlanOptimizationProgress } from './rfOptimizerProgress'

export type OptimizeFloorPlanOptions = {
  inventoryAps?: { id: string; name: string }[]
  expectedClients?: number
  maxNewAps?: number
  onProgress?: (progress: FloorPlanOptimizationProgress) => void
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

function withChannels(aps: PlacedAp[], channels: Record<string, number>): PlacedAp[] {
  return aps.map(ap => ({ ...ap, channel: channels[ap.id] ?? ap.channel }))
}

export { computeFloorMetrics, optimizationGrid } from './rfFloorMetrics'

function separatePosition(
  anchor: { x: number; y: number },
  other: { x: number; y: number },
  targetSepM: number,
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
): { x: number; y: number } {
  let dxM = ((other.x - anchor.x) / 100) * floorWidthM
  let dyM = ((other.y - anchor.y) / 100) * floorHeightM
  let distM = Math.hypot(dxM, dyM)
  if (distM < 0.5) {
    dxM = targetSepM
    dyM = 0
    distM = targetSepM
  }
  if (distM >= targetSepM) return { x: other.x, y: other.y }
  const scale = targetSepM / distM
  return clampToFloorBounds(
    anchor.x + ((dxM * scale) / floorWidthM) * 100,
    anchor.y + ((dyM * scale) / floorHeightM) * 100,
    geometry,
  )
}

function closePairs(aps: PlacedAp[], floorWidthM: number, floorHeightM: number, maxDistM: number) {
  const pairs: { a: PlacedAp; b: PlacedAp; distM: number }[] = []
  for (let i = 0; i < aps.length; i++) {
    for (let j = i + 1; j < aps.length; j++) {
      const distM = distanceM(aps[i].x, aps[i].y, aps[j].x, aps[j].y, floorWidthM, floorHeightM)
      if (distM < maxDistM) pairs.push({ a: aps[i], b: aps[j], distM })
    }
  }
  return pairs.sort((x, y) => x.distM - y.distM)
}

function sampleAroundAp(
  ap: PlacedAp,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  radiusM: number,
): { weakPct: number; highInterfPct: number } {
  const steps = 8
  let weak = 0
  let high = 0
  let n = 0
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * Math.PI * 2
    const px = ap.x + ((Math.cos(angle) * radiusM) / floorWidthM) * 100
    const py = ap.y + ((Math.sin(angle) * radiusM) / floorHeightM) * 100
    if (!pointInFloorBounds(px, py, geometry)) continue
    const m = rfMetricsAtPoint(px, py, aps, geometry, floorWidthM, floorHeightM)
    if (m.rssi < -75) weak++
    if (m.interference >= 55) high++
    n++
  }
  return { weakPct: n ? (weak / n) * 100 : 0, highInterfPct: n ? (high / n) * 100 : 0 }
}

function optimizeSeparation(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  weakCentroid: { x: number; y: number } | null = null,
): PlacedAp[] {
  const next = ensureApMountDefaults(working.map(ap => ({ ...ap })))
  for (const pair of closePairs(next, floorWidthM, floorHeightM, 14)) {
    const aIdx = next.findIndex(ap => ap.id === pair.a.id)
    const bIdx = next.findIndex(ap => ap.id === pair.b.id)
    if (aIdx < 0 || bIdx < 0) continue
    const targetSepM = pair.distM < 8 ? 18 : pair.distM < 11 ? 16 : 14
    const moveIdx = (next[aIdx].clients ?? 0) <= (next[bIdx].clients ?? 0) ? bIdx : aIdx
    const anchorIdx = moveIdx === bIdx ? aIdx : bIdx
    const anchor = next[anchorIdx]
    const moveTarget = next[moveIdx]
    const raw = separatePosition(anchor, moveTarget, targetSepM, floorWidthM, floorHeightM, geometry)
    const reconciled = reconcileApAfterMove(
      moveTarget,
      raw.x,
      raw.y,
      geometry,
      floorWidthM,
      floorHeightM,
      weakCentroid,
    )
    if (
      Math.hypot(moveTarget.x - reconciled.x, moveTarget.y - reconciled.y) > 0.5
      || Math.abs((moveTarget.mountOrientationDeg ?? 0) - (reconciled.mountOrientationDeg ?? 0)) > 5
    ) {
      next[moveIdx] = reconciled
    }
  }
  return next
}

function weakCentroidForAps(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): { x: number; y: number } | null {
  const grid = computeHeatmapGrid(aps, geometry, floorWidthM, floorHeightM, RF_GRID_COLS, RF_GRID_ROWS)
  return weakZoneCentroid(grid, geometry, floorWidthM, floorHeightM)
}

function plannedApFromNew(
  n: FloorPlanNewAp,
  i: number,
  idPrefix: string,
): PlacedAp {
  return {
    id: `${idPrefix}-${i}`,
    ap: n.apName,
    x: n.x,
    y: n.y,
    txPowerDbm: n.txPowerDbm,
    channel: n.channel,
    mount: n.mount,
    mountHeightM: n.mountHeightM,
    mountOrientationDeg: n.mountOrientationDeg,
  }
}

function optimizeTxPowerForCoverage(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  const floor = computeFloorMetrics(working, geometry, floorWidthM, floorHeightM)
  if (floor.weakPct <= 15 || floor.highInterfPct >= 16) return working

  let next = working.map(ap => ({ ...ap }))
  for (let pass = 0; pass < 4; pass++) {
    const before = computeFloorMetrics(next, geometry, floorWidthM, floorHeightM)
    if (before.weakPct <= 15 || before.highInterfPct >= 16) break
    let improved = false
    for (let i = 0; i < next.length; i++) {
      const tx = next[i].txPowerDbm ?? 15
      if (tx >= 18) continue
      const local = sampleAroundAp(next[i], next, geometry, floorWidthM, floorHeightM, 12)
      if (local.highInterfPct >= 28) continue
      const trial = next.map((a, j) =>
        j === i ? { ...a, txPowerDbm: Math.min(18, tx + 2) } : a,
      )
      const after = computeFloorMetrics(trial, geometry, floorWidthM, floorHeightM)
      if (
        after.weakPct < before.weakPct - 0.35 &&
        after.highInterfPct <= before.highInterfPct + 1.5
      ) {
        next = trial
        improved = true
        break
      }
    }
    if (!improved) break
  }
  return next
}

function optimizeCoverageLayout(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  let next = optimizeMountDirectionAndRedistribute(
    optimizePlacementPhase(working, geometry, floorWidthM, floorHeightM),
    geometry,
    floorWidthM,
    floorHeightM,
  )
  next = optimizeTxPowerForCoverage(next, geometry, floorWidthM, floorHeightM)
  return next
}

function optimizePlacementPhase(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  return optimizeGeometryAwarePositions(ensureApMountDefaults(working), geometry, floorWidthM, floorHeightM)
}

function optimizeMountDirectionAndRedistribute(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  let next = optimizeWallMountOrientations(working, geometry, floorWidthM, floorHeightM)
  const metrics = computeFloorMetrics(next, geometry, floorWidthM, floorHeightM)
  if (metrics.weakPct > 18 && next.length >= 2) {
    const redistributed = redistributeApsGeometryAware(next, geometry, floorWidthM, floorHeightM)
    const after = computeFloorMetrics(redistributed, geometry, floorWidthM, floorHeightM)
    if (after.weakPct < metrics.weakPct - 1) {
      next = optimizeWallMountOrientations(redistributed, geometry, floorWidthM, floorHeightM)
    }
  }
  return next
}

function optimizeTxPower(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  const floorBefore = computeFloorMetrics(working, geometry, floorWidthM, floorHeightM)
  const aggressive = floorBefore.highInterfPct >= 30
  let next = working.map(ap => ({ ...ap }))

  for (let i = 0; i < next.length; i++) {
    const ap = next[i]
    let tx = ap.txPowerDbm ?? 15
    const startTx = tx
    while (tx > 10) {
      const candidate = tx - 2
      const trial = next.map((a, j) =>
        j === i ? { ...a, txPowerDbm: candidate } : a,
      )
      const local = sampleAroundAp(ap, trial, geometry, floorWidthM, floorHeightM, 14)
      const floorAfter = computeFloorMetrics(trial, geometry, floorWidthM, floorHeightM)
      if (local.weakPct > (aggressive ? 18 : 12)) break
      if (floorAfter.weakPct > floorBefore.weakPct + MAX_WEAK_RISE_PCT) break
      if (floorAfter.goodPct < floorBefore.goodPct - MAX_GOOD_DROP_PCT) break
      if (local.highInterfPct < (aggressive ? 22 : 38) && candidate <= 15) break
      if (local.highInterfPct < (aggressive ? 18 : 28)) break
      tx = candidate
      next = trial
    }
    if (tx === startTx && startTx > 12) {
      const trial = next.map((a, j) =>
        j === i ? { ...a, txPowerDbm: startTx - 2 } : a,
      )
      const local = sampleAroundAp(ap, trial, geometry, floorWidthM, floorHeightM, 14)
      const floorAfter = computeFloorMetrics(trial, geometry, floorWidthM, floorHeightM)
      if (
        local.weakPct <= 12
        && floorAfter.weakPct <= floorBefore.weakPct + MAX_WEAK_RISE_PCT
        && floorAfter.goodPct >= floorBefore.goodPct - MAX_GOOD_DROP_PCT
      ) {
        tx = startTx - 2
        next = trial
      }
    }
    next[i] = { ...next[i], txPowerDbm: Math.max(10, tx) }
  }
  return next
}

function targetApCount(
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
  expectedClients: number,
): number {
  const area = floorWidthM * floorHeightM
  const wallFactor = 1 + geometry.walls.length * 0.04 + geometry.obstacles.length * 0.03
  const byArea = Math.ceil(area / (950 * wallFactor))
  const byClients = expectedClients > 0 ? Math.ceil(expectedClients / 28) : 0
  return Math.max(1, byArea, byClients)
}

function originalChannelMap(aps: PlacedAp[]): Record<string, number> {
  const ch: Record<string, number> = {}
  for (const ap of aps) {
    if (ap.channel != null) ch[ap.id] = ap.channel
  }
  return ch
}

function restoreOriginalLayout(working: PlacedAp[], original: PlacedAp[]): PlacedAp[] {
  return working.map(ap => {
    const orig = original.find(o => o.id === ap.id)
    if (!orig) return ap
    return {
      ...ap,
      x: orig.x,
      y: orig.y,
      txPowerDbm: orig.txPowerDbm ?? 15,
      mountOrientationDeg: orig.mountOrientationDeg,
      mountHeightM: orig.mountHeightM,
      channel: orig.channel,
    }
  })
}

type OptimizationCandidate = {
  working: PlacedAp[]
  newAps: FloorPlanNewAp[]
  channels: Record<string, number>
  metrics: FloorPlanMetrics
}

function buildOptimizationCandidate(
  working: PlacedAp[],
  newAps: FloorPlanNewAp[],
  channels: Record<string, number>,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): OptimizationCandidate {
  const planned: PlacedAp[] = newAps.map((n, i) => plannedApFromNew(n, i, 'planned'))
  const finalWorking = withChannels(
    working.filter(w => !w.id.startsWith('new-') && !w.id.startsWith('virt-')),
    channels,
  )
  const metrics = computeFloorMetrics(
    [...finalWorking, ...planned],
    geometry,
    floorWidthM,
    floorHeightM,
  )
  return { working: finalWorking, newAps, channels, metrics }
}

function prepareBaseline(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): { original: PlacedAp[]; metricsBefore: FloorPlanMetrics } {
  let original = syncApChannels(aps.map(ap => ({ ...ap })), geometry)
  const missingChannel = original.some(ap => ap.channel == null)
  if (missingChannel && original.length >= 2) {
    const seeded = assignOptimalChannels(original, geometry.bandGHz, floorWidthM, floorHeightM)
    original = withChannels(original, seeded)
  }
  const metricsBefore = computeFloorMetrics(original, geometry, floorWidthM, floorHeightM)
  return { original, metricsBefore }
}

function metricsWithNewAps(
  working: PlacedAp[],
  newAps: FloorPlanNewAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): FloorPlanMetrics {
  const planned = newAps.map((n, i) => plannedApFromNew(n, i, 'metric'))
  return computeFloorMetrics([...working, ...planned], geometry, floorWidthM, floorHeightM)
}

function pickBestOptimizationCandidate(
  candidates: OptimizationCandidate[],
  metricsBefore: FloorPlanMetrics,
  channelsOnlyCandidate: OptimizationCandidate,
  originalCandidate: OptimizationCandidate,
): OptimizationCandidate {
  const acceptable = candidates.filter(c => planAcceptable(c.metrics, metricsBefore))
  if (acceptable.length > 0) {
    const improving = acceptable.filter(c => planImproves(c.metrics, metricsBefore))
    const pool = improving.length > 0 ? improving : acceptable
    return pool.reduce((best, cur) =>
      candidateRank(cur.metrics, metricsBefore) > candidateRank(best.metrics, metricsBefore)
        ? cur
        : best,
    )
  }

  if (
    planAcceptable(channelsOnlyCandidate.metrics, metricsBefore)
    && channelsOnlyCandidate.metrics.highInterfPct <= metricsBefore.highInterfPct - 1
  ) {
    return channelsOnlyCandidate
  }

  if (metricsBefore.highInterfPct >= 20) {
    const interfRelief = candidates.filter(
      c =>
        planAcceptable(c.metrics, metricsBefore)
        && c.metrics.highInterfPct < metricsBefore.highInterfPct - 0.5,
    )
    if (interfRelief.length > 0) {
      return interfRelief.reduce((best, cur) =>
        cur.metrics.highInterfPct < best.metrics.highInterfPct ? cur : best,
      )
    }
  }

  return originalCandidate
}

function maxGapApAdds(
  working: PlacedAp[],
  needed: number,
  midMetrics: FloorPlanMetrics,
  maxNewAps: number,
  baselineInterfPct: number,
): number {
  if (baselineInterfPct >= 8 || midMetrics.highInterfPct >= 12) return 0
  const deficit = Math.max(0, needed - working.length)
  if (deficit <= 0 && midMetrics.weakPct <= 18) return 0
  let cap = maxNewAps
  if (midMetrics.highInterfPct > 8) cap = Math.min(cap, 1)
  else if (midMetrics.highInterfPct > 5) cap = Math.min(cap, 2)
  if (midMetrics.weakPct > 12) {
    cap = Math.min(cap, Math.max(deficit, Math.ceil(midMetrics.weakPct / 30)))
  } else {
    cap = Math.min(cap, deficit)
  }
  return Math.max(0, cap)
}

function tryAddGapAps(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  maxAdd: number,
  inventoryAps: { id: string; name: string; model?: string }[] | undefined,
  baselineInterfPct: number,
): { working: PlacedAp[]; newAps: FloorPlanNewAp[] } {
  const newAps: FloorPlanNewAp[] = []
  let sim = ensureApMountDefaults(working.map(ap => ({ ...ap })))
  const maxInterfPct = Math.min(18, baselineInterfPct + MAX_INTERF_RISE_PCT)

  for (let i = 0; i < maxAdd; i++) {
    const before = computeFloorMetrics(sim, geometry, floorWidthM, floorHeightM)
    if (before.highInterfPct >= 12) break

    const grid = optimizationGrid(sim, geometry, floorWidthM, floorHeightM)
    const pos = findGapApPosition(sim, grid, floorWidthM, floorHeightM, geometry)
    if (!pos) break

    const apName = pickInventoryName(inventoryAps, [
      ...sim,
      ...newAps.map(n => ({ id: n.apName, ap: n.apName, x: 0, y: 0 })),
    ])
    if (!apName) break

    const placement = buildGapApPlacement(
      apName,
      inventoryAps,
      pos,
      sim,
      geometry,
      floorWidthM,
      floorHeightM,
    )
    const placed = simulatedNewApToPlacedAp(placement, `new-${i}`)

    const virtualId = `new-${i}`
    const trial = [...sim, { ...placed, id: virtualId }]
    const ch = assignOptimalChannels(trial, geometry.bandGHz, floorWidthM, floorHeightM)
    const trialWithCh = withChannels(trial, ch)
    const after = computeFloorMetrics(trialWithCh, geometry, floorWidthM, floorHeightM)

    const weakGain = before.weakPct - after.weakPct
    const interfRise = after.highInterfPct - before.highInterfPct

    if (weakGain < 3) break
    if (interfRise > 2) break
    if (after.highInterfPct > maxInterfPct) break
    if (after.score < before.score - 0.25) break

    sim = trialWithCh
    newAps.push({
      apName,
      x: placed.x,
      y: placed.y,
      txPowerDbm: placement.txPowerDbm,
      channel: ch[virtualId] ?? channelPoolForBand(geometry.bandGHz)[i % 12],
      mount: placed.mount,
      mountHeightM: placed.mountHeightM,
      mountOrientationDeg: placed.mountOrientationDeg,
    })
  }

  return { working: sim, newAps }
}

function pickInventoryName(
  inventoryAps: { id: string; name: string }[] | undefined,
  placed: PlacedAp[],
): string | null {
  if (!inventoryAps?.length) return null
  const onFloor = new Set(placed.map(a => a.ap))
  return inventoryAps.find(a => !onFloor.has(a.name))?.name ?? null
}

function suggestNewApPositions(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  count: number,
): { x: number; y: number }[] {
  return tryAddGapAps(
    working,
    geometry,
    floorWidthM,
    floorHeightM,
    count,
    undefined,
    computeFloorMetrics(working, geometry, floorWidthM, floorHeightM).highInterfPct,
  ).newAps.map(n => ({ x: n.x, y: n.y }))
}

function buildSummary(
  before: FloorPlanMetrics,
  after: FloorPlanMetrics,
  changes: FloorPlanOptimizationChange[],
  newAps: FloorPlanNewAp[],
  bandGHz: number,
  geometry: FloorGeometry,
  addressedRecommendations: string[],
  remainingRecommendations: FloorRecommendation[],
): string[] {
  const lines: string[] = []
  const geo = geometryModelSummary(geometry)
  if (geo) lines.push(`RF model included ${geo}, AP mount height, and wall boresight.`)

  const crop = geometry.backgroundCrop
  const hasUserCrop =
    (crop?.top ?? 0) > 0 ||
    (crop?.bottom ?? 0) > 0 ||
    (crop?.left ?? 0) > 0 ||
    (crop?.right ?? 0) > 0
  if (hasUserCrop) {
    lines.push('Floor plan image crop applied — AP placement uses the cropped floor area.')
  }

  const moved = changes.filter(c => c.x != null || c.y != null).length
  const txChanged = changes.filter(c => c.txPowerDbm != null).length
  const chChanged = changes.filter(c => c.channel != null).length
  const orientChanged = changes.filter(c => c.mountOrientationDeg != null).length
  if (moved > 0) {
    lines.push(`Repositioned ${moved} AP${moved > 1 ? 's' : ''} using wall/obstacle-aware RF simulation.`)
  }
  if (orientChanged > 0) {
    lines.push(`Aligned ${orientChanged} wall-mount AP${orientChanged > 1 ? 's' : ''} toward coverage targets.`)
  }
  if (txChanged > 0) {
    const boosted = changes.some(c => (c.txPowerDbm ?? 0) >= 17)
    lines.push(
      boosted
        ? `Tuned TX power on ${txChanged} AP${txChanged > 1 ? 's' : ''} to extend coverage into weak zones.`
        : `Adjusted TX power on ${txChanged} AP${txChanged > 1 ? 's' : ''} for cell sizing.`,
    )
  }
  if (chChanged > 0) {
    const pool = channelPoolForBand(bandGHz)
    const interfDelta = after.highInterfPct - before.highInterfPct
    if (interfDelta <= -2) {
      lines.push(
        `Assigned ${bandGHz} GHz channels from pool [${pool.slice(0, 4).join(', ')}…] — interference down ${Math.abs(Math.round(interfDelta))}%.`,
      )
    } else if (interfDelta >= 3) {
      lines.push(
        `Assigned ${bandGHz} GHz channels — interference rose ${Math.round(interfDelta)}% (check AP spacing / TX power).`,
      )
    } else {
      lines.push(`Assigned ${bandGHz} GHz channels from pool [${pool.slice(0, 4).join(', ')}…].`)
    }
  }
  if (newAps.length > 0) {
    lines.push(`Added ${newAps.length} AP${newAps.length > 1 ? 's' : ''} in weak areas (≥12 m from others).`)
  }
  if (addressedRecommendations.length > 0) {
    lines.push(
      `Addressed ${addressedRecommendations.length} RF recommendation${addressedRecommendations.length > 1 ? 's' : ''}: ${addressedRecommendations.slice(0, 3).join('; ')}${addressedRecommendations.length > 3 ? '…' : ''}.`,
    )
  }
  const manualRemaining = remainingRecommendations.filter(
    r => r.action === 'draw_walls' || r.action === 'draw_obstacles',
  )
  if (manualRemaining.length > 0) {
    lines.push(
      `Still recommended: ${manualRemaining.map(r => r.title).slice(0, 2).join('; ')} — use the RF panel to apply.`,
    )
  }
  lines.push(
    `Coverage: ${Math.round(before.goodPct)}% → ${Math.round(after.goodPct)}% good cells · weak ${Math.round(before.weakPct)}% → ${Math.round(after.weakPct)}%.`,
  )
  lines.push(
    `Interference: ${Math.round(before.highInterfPct)}% → ${Math.round(after.highInterfPct)}% high-overlap cells.`,
  )
  return lines
}

/** Simulation-in-the-loop floor optimizer: placement, TX power, channels, and gap-fill APs. */
export async function optimizeFloorPlan(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  options: OptimizeFloorPlanOptions = {},
): Promise<FloorPlanOptimizationResult> {
  const { inventoryAps, expectedClients = 0, maxNewAps = 3, onProgress } = options
  const startedAt = Date.now()
  let stepNum = 0

  async function step(
    phase: Parameters<typeof makeOptimizationProgress>[0],
    detail?: string,
    work?: () => void,
  ) {
    stepNum += 1
    await paceOptimizationStep(stepNum, startedAt, onProgress, phase, detail, work)
  }

  let original: PlacedAp[] = []
  let metricsBefore = computeFloorMetrics(aps, geometry, floorWidthM, floorHeightM)

  await step(
    'inventory',
    `${aps.length} AP${aps.length !== 1 ? 's' : ''} on floor · ${inventoryAps?.length ?? 0} in inventory`,
    () => {
      const baseline = prepareBaseline(aps, geometry, floorWidthM, floorHeightM)
      original = baseline.original
      metricsBefore = baseline.metricsBefore
    },
  )

  if (original.length === 0) {
    return {
      changes: [],
      newAps: [],
      channels: {},
      metricsBefore,
      metricsAfter: metricsBefore,
      summary: ['Place at least one AP on the floor before optimizing.'],
      addressedRecommendations: [],
      remainingRecommendations: [],
    }
  }

  const wallCount = geometry.walls.length
  const obsCount = geometry.obstacles.length
  const roomCount = geometry.rooms.length
  const geoParts = [
    wallCount > 0 ? `${wallCount} wall${wallCount !== 1 ? 's' : ''}` : null,
    obsCount > 0 ? `${obsCount} obstacle${obsCount !== 1 ? 's' : ''}` : null,
    roomCount > 0 ? `${roomCount} room${roomCount !== 1 ? 's' : ''}` : null,
  ].filter(Boolean)
  await step(
    'geometry',
    geoParts.length > 0 ? geoParts.join(' · ') : 'Open floor — no walls drawn yet',
  )

  await step(
    'baseline',
    `${Math.round(metricsBefore.weakPct)}% weak · ${Math.round(metricsBefore.goodPct)}% good coverage`,
  )

  let working = ensureApMountDefaults(original.map(ap => ({ ...ap })))
  let channels = originalChannelMap(original)
  if (Object.keys(channels).length < working.length && working.length >= 2) {
    const seeded = assignOptimalChannels(working, geometry.bandGHz, floorWidthM, floorHeightM)
    channels = { ...channels, ...seeded }
  }
  working = withChannels(working, channels)
  const rfProfile = interferenceDominant(metricsBefore) ? 'interference' as const : 'balanced' as const
  let newAps: FloorPlanNewAp[] = []
  let addressedRecommendations: string[] = []
  let remainingRecommendations: FloorRecommendation[] = []

  await step(
    'recommendations',
    rfProfile === 'interference'
      ? 'Applying safe overlap fixes (TX + channels, keeping positions)'
      : 'Applying RF panel recommendations (overlap, coverage, TX)',
    () => {
      const recResult = applyOptimizerRecommendations(
        working,
        geometry,
        floorWidthM,
        floorHeightM,
        { inventoryAps, maxNewAps, metricsBefore, interferenceOnly: rfProfile === 'interference' },
      )
      working = recResult.aps
      if (Object.keys(recResult.channels).length > 0) {
        channels = recResult.channels
        working = withChannels(working, channels)
      }
      newAps = recResult.newAps.map(n => ({ ...n }))
      addressedRecommendations = recResult.addressed
      remainingRecommendations = recResult.remaining
    },
  )

  await step(
    'placement',
    rfProfile === 'interference'
      ? 'Keeping AP positions — overlap floor, channels/TX only'
      : 'Snapping to walls, avoiding obstacles, targeting weak zones',
    () => {
      if (rfProfile === 'interference') return
      const weakCentroid = weakCentroidForAps(working, geometry, floorWidthM, floorHeightM)
      working = optimizeSeparation(working, geometry, floorWidthM, floorHeightM, weakCentroid)
      working = optimizePlacementPhase(working, geometry, floorWidthM, floorHeightM)
    },
  )

  await step(
    'direction',
    rfProfile === 'interference'
      ? 'Aligning wall-mount boresight (positions unchanged)'
      : 'Aligning wall-mount boresight and mount heights',
    () => {
      if (rfProfile !== 'interference') {
        working = optimizeMountDirectionAndRedistribute(working, geometry, floorWidthM, floorHeightM)
      } else if (working.some(ap => normalizeMountType(ap.mount) === 'wall')) {
        working = optimizeWallMountOrientations(working, geometry, floorWidthM, floorHeightM)
      }
    },
  )

  await step(
    'channels',
    rfProfile === 'interference'
      ? `${geometry.bandGHz} GHz channel plan — positions unchanged`
      : `${geometry.bandGHz} GHz channel plan for co-channel separation`,
    () => {
      if (rfProfile !== 'interference') {
        const weakCentroid = weakCentroidForAps(working, geometry, floorWidthM, floorHeightM)
        working = optimizeSeparation(working, geometry, floorWidthM, floorHeightM, weakCentroid)
        working = optimizeWallMountOrientations(working, geometry, floorWidthM, floorHeightM)
      }
      channels = assignOptimalChannels(working, geometry.bandGHz, floorWidthM, floorHeightM)
      working = withChannels(working, channels)
    },
  )

  await step(
    'power',
    rfProfile === 'interference'
      ? 'Applying TX cuts only where overlap remains high'
      : 'Balancing cell size vs co-channel overlap',
    () => {
      if (rfProfile === 'interference') {
        working = optimizeTxPower(working, geometry, floorWidthM, floorHeightM)
        return
      }
      working = optimizeTxPower(working, geometry, floorWidthM, floorHeightM)
      const afterReduce = computeFloorMetrics(working, geometry, floorWidthM, floorHeightM)
      if (afterReduce.highInterfPct < 12 && afterReduce.weakPct > 20) {
        working = optimizeTxPowerForCoverage(working, geometry, floorWidthM, floorHeightM)
      }
    },
  )

  const midMetrics = metricsWithNewAps(working, newAps, geometry, floorWidthM, floorHeightM)
  const needed = targetApCount(floorWidthM, floorHeightM, geometry, expectedClients)

  await step(
    'gaps',
    rfProfile === 'interference'
      ? 'Skipping AP adds — reducing overlap without shrinking cells'
      : midMetrics.weakPct > 12
        ? `${Math.round(midMetrics.weakPct)}% weak — checking spare inventory`
        : 'Coverage within target — verifying AP count',
    () => {
      if (rfProfile === 'interference') return
      const addBudget = maxGapApAdds(
        working,
        needed,
        midMetrics,
        Math.max(0, maxNewAps - newAps.length),
        metricsBefore.highInterfPct,
      )
      if (addBudget > 0 && inventoryAps?.length) {
        const gapResult = tryAddGapAps(
          working,
          geometry,
          floorWidthM,
          floorHeightM,
          addBudget,
          inventoryAps,
          metricsBefore.highInterfPct,
        )
        if (gapResult.newAps.length > 0) {
          const trialNew = [...newAps, ...gapResult.newAps]
          const trialMetrics = metricsWithNewAps(working, trialNew, geometry, floorWidthM, floorHeightM)
          if (planAcceptable(trialMetrics, metricsBefore)) {
            newAps.push(...gapResult.newAps)
            const planAps: PlacedAp[] = [
              ...working,
              ...newAps.map((n, i) => plannedApFromNew(n, i, 'plan')),
            ]
            channels = assignOptimalChannels(planAps, geometry.bandGHz, floorWidthM, floorHeightM)
            working = withChannels(working, channels)
            for (let i = 0; i < newAps.length; i++) {
              const ch = channels[`plan-${i}`]
              if (ch != null) newAps[i].channel = ch
            }
          }
        }
      }
    },
  )

  if (newAps.length > 0) {
    channels = assignOptimalChannels(
      [...working, ...newAps.map((n, i) => plannedApFromNew(n, i, 'plan'))],
      geometry.bandGHz,
      floorWidthM,
      floorHeightM,
    )
    working = withChannels(working, channels)
  }

  let metricsAfter = metricsBefore
  let changes: FloorPlanOptimizationChange[] = []
  let summary: string[] = []

  await step(
    'finalize',
    'Scoring before/after RF health',
    () => {
      const fullCandidate = buildOptimizationCandidate(
        working,
        newAps,
        channels,
        geometry,
        floorWidthM,
        floorHeightM,
      )
      const noNewCandidate = buildOptimizationCandidate(
        working,
        [],
        channels,
        geometry,
        floorWidthM,
        floorHeightM,
      )

      const restored = restoreOriginalLayout(
        original.map(ap => ({ ...ap })),
        original,
      )
      const origChannels = originalChannelMap(original)
      const originalCandidate = buildOptimizationCandidate(
        restored,
        [],
        origChannels,
        geometry,
        floorWidthM,
        floorHeightM,
      )

      const optChannels = assignOptimalChannels(
        restored,
        geometry.bandGHz,
        floorWidthM,
        floorHeightM,
      )
      const channelsOnlyCandidate = buildOptimizationCandidate(
        withChannels(restored, optChannels),
        [],
        optChannels,
        geometry,
        floorWidthM,
        floorHeightM,
      )

      const interferenceTunedCandidate = buildOptimizationCandidate(
        optimizeTxPower(withChannels(restored, optChannels), geometry, floorWidthM, floorHeightM),
        [],
        optChannels,
        geometry,
        floorWidthM,
        floorHeightM,
      )

      const picked = pickBestOptimizationCandidate(
        [
          fullCandidate,
          noNewCandidate,
          channelsOnlyCandidate,
          interferenceTunedCandidate,
          originalCandidate,
        ],
        metricsBefore,
        channelsOnlyCandidate,
        originalCandidate,
      )

      const keptOriginalLayout =
        picked.newAps.length === 0
        && picked.working.every(ap => {
          const orig = original.find(o => o.id === ap.id)
          if (!orig) return true
          return (
            Math.hypot(orig.x - ap.x, orig.y - ap.y) <= 0.5
            && Math.abs((orig.txPowerDbm ?? 15) - (ap.txPowerDbm ?? 15)) < 1
            && Math.abs((orig.mountOrientationDeg ?? 0) - (ap.mountOrientationDeg ?? 0)) <= 5
          )
        })

      const finalWorking = picked.working
      newAps.length = 0
      newAps.push(...picked.newAps)
      channels = { ...picked.channels }
      metricsAfter = picked.metrics

      const rejectedNewAps =
        fullCandidate.newAps.length > 0 && picked.newAps.length === 0
      const rolledBackLayout =
        picked.newAps.length === 0
        && picked.working.every(ap => {
          const orig = original.find(o => o.id === ap.id)
          if (!orig) return true
          return (
            Math.hypot(orig.x - ap.x, orig.y - ap.y) <= 0.5
            && Math.abs((orig.txPowerDbm ?? 15) - (ap.txPowerDbm ?? 15)) < 1
          )
        })
        && (
          fullCandidate.metrics.highInterfPct > metricsBefore.highInterfPct + MAX_INTERF_RISE_PCT
          || !coverageOk(fullCandidate.metrics, metricsBefore)
        )

      changes = []
      for (const orig of original) {
        const opt = finalWorking.find(w => w.id === orig.id)
        if (!opt) continue
        const ch = channels[orig.id]
        const change: FloorPlanOptimizationChange = { apId: orig.id }
        let dirty = false
        if (Math.hypot(orig.x - opt.x, orig.y - opt.y) > 0.5) {
          change.x = opt.x
          change.y = opt.y
          dirty = true
        }
        const origTx = orig.txPowerDbm ?? 15
        const optTx = opt.txPowerDbm ?? 15
        if (Math.abs(origTx - optTx) >= 1) {
          change.txPowerDbm = optTx
          dirty = true
        }
        if (ch != null && ch !== orig.channel) {
          change.channel = ch
          dirty = true
        }
        const origOrient = orig.mountOrientationDeg ?? 0
        const optOrient = opt.mountOrientationDeg ?? 0
        if (normalizeMountType(orig.mount) === 'wall' && Math.abs(origOrient - optOrient) > 5) {
          change.mountOrientationDeg = optOrient
          dirty = true
        }
        const origH = orig.mountHeightM ?? defaultMountHeight(orig.mount)
        const optH = opt.mountHeightM ?? defaultMountHeight(opt.mount)
        if (Math.abs(origH - optH) > 0.2) {
          change.mountHeightM = optH
          dirty = true
        }
        if (dirty) changes.push(change)
      }

      summary = buildSummary(
        metricsBefore,
        metricsAfter,
        changes,
        newAps,
        geometry.bandGHz,
        geometry,
        addressedRecommendations,
        remainingRecommendations,
      )
      if (rejectedNewAps) {
        summary.unshift(
          'Skipped adding APs — projected co-channel overlap exceeded safe limits for this floor.',
        )
      }
      if (rolledBackLayout) {
        summary.unshift(
          'Reverted placement and TX changes — they would have hurt coverage or raised co-channel overlap.',
        )
      }
      if (keptOriginalLayout && !planImproves(metricsAfter, metricsBefore)) {
        summary.unshift(
          'No safe improvements found — kept your current AP layout and power settings.',
        )
      } else if (keptOriginalLayout && planImproves(metricsAfter, metricsBefore)) {
        summary.unshift('Applied channel/TX tuning only — AP positions unchanged.')
      }
      const weakImproved = metricsBefore.weakPct - metricsAfter.weakPct >= 2
      if (
        planImproves(metricsAfter, metricsBefore)
        && !keptOriginalLayout
        && !weakImproved
        && metricsAfter.weakPct > 15
      ) {
        summary.unshift(
          `Weak zones remain at ${Math.round(metricsAfter.weakPct)}% — add APs from inventory or draw walls for accurate modeling.`,
        )
      }
    },
  )

  if (!planAcceptable(metricsAfter, metricsBefore)) {
    const safeChannels = assignOptimalChannels(
      restoreOriginalLayout(original.map(ap => ({ ...ap })), original),
      geometry.bandGHz,
      floorWidthM,
      floorHeightM,
    )
    const safeWorking = withChannels(
      restoreOriginalLayout(original.map(ap => ({ ...ap })), original),
      safeChannels,
    )
    const safeMetrics = computeFloorMetrics(safeWorking, geometry, floorWidthM, floorHeightM)
    if (planAcceptable(safeMetrics, metricsBefore) && planImproves(safeMetrics, metricsBefore)) {
      const safeChanges: FloorPlanOptimizationChange[] = []
      for (const orig of original) {
        const ch = safeChannels[orig.id]
        if (ch != null && ch !== orig.channel) {
          safeChanges.push({ apId: orig.id, channel: ch })
        }
      }
      return {
        changes: safeChanges,
        newAps: [],
        channels: safeChannels,
        metricsBefore,
        metricsAfter: safeMetrics,
        summary: [
          'Applied channel plan only — full optimization exceeded safety limits.',
          `Coverage: ${Math.round(metricsBefore.goodPct)}% → ${Math.round(safeMetrics.goodPct)}% good · weak ${Math.round(metricsBefore.weakPct)}% → ${Math.round(safeMetrics.weakPct)}%.`,
          `Interference: ${Math.round(metricsBefore.highInterfPct)}% → ${Math.round(safeMetrics.highInterfPct)}% high-overlap cells.`,
        ],
        addressedRecommendations,
        remainingRecommendations,
      }
    }
    return {
      changes: [],
      newAps: [],
      channels: originalChannelMap(original),
      metricsBefore,
      metricsAfter: metricsBefore,
      summary: [
        'Optimization aborted — no plan met coverage and interference safety limits.',
        `Coverage: ${Math.round(metricsBefore.goodPct)}% good · weak ${Math.round(metricsBefore.weakPct)}%.`,
        `Interference: ${Math.round(metricsBefore.highInterfPct)}% high-overlap cells.`,
      ],
      addressedRecommendations: [],
      remainingRecommendations,
    }
  }

  return {
    changes,
    newAps,
    channels,
    metricsBefore,
    metricsAfter,
    summary,
    addressedRecommendations,
    remainingRecommendations,
  }
}

/** Recompute after-metrics from APs actually on the floor so UI matches live RF health. */
export function applyOptimizationMetrics(
  result: FloorPlanOptimizationResult,
  appliedAps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): FloorPlanOptimizationResult {
  const metricsAfter = computeFloorMetrics(appliedAps, geometry, floorWidthM, floorHeightM)
  const summary = result.summary.map(line => {
    if (line.startsWith('Coverage:')) {
      return `Coverage: ${Math.round(result.metricsBefore.goodPct)}% → ${Math.round(metricsAfter.goodPct)}% good cells · weak ${Math.round(result.metricsBefore.weakPct)}% → ${Math.round(metricsAfter.weakPct)}%.`
    }
    if (line.startsWith('Interference:')) {
      return `Interference: ${Math.round(result.metricsBefore.highInterfPct)}% → ${Math.round(metricsAfter.highInterfPct)}% high-overlap cells.`
    }
    return line
  })
  return { ...result, metricsAfter, summary }
}
